import { useTranslation } from 'react-i18next';
import useEvents from 'lib/hooks/useEvents';
import DistanceListTapEvent from 'lib/Tracking/Srp/SearchFilters/LocationFilter/DistanceListTap';
import EditLocationTapEvent from 'lib/Tracking/Srp/SearchFilters/LocationFilter/EditLocationTap';
import EnterCityZipTapEvent from 'lib/Tracking/Srp/SearchFilters/LocationFilter/EnterCityZipTap';
import CurrentLocationTapEvent from 'lib/Tracking/Srp/SearchFilters/LocationFilter/CurrentLocationTap';
import SelectCityTapEvent from 'lib/Tracking/Srp/SearchFilters/LocationFilter/SelectCityTap';
import ExpandCollapseTap from 'lib/Tracking/Srp/SearchFilters/ExpandCollapseTap';
import { Features } from 'lib/EventSchema';
import RadiusTapEvent from 'lib/Tracking/Srp/SearchFilters/LocationFilter/RadiusTap';

import FilterContainer from 'components/Shared/SearchFilter/FilterContainer';
import LocationInputNoDialog, { getLocationLabel } from 'components/LocationInputNoDialog';
import ModalDropdownContainer from 'components/Shared/SearchFilter/ModalDropdownContainer';
import DropdownContainer from 'components/Shared/SearchFilter/DropdownContainer';
import RadiusModal, { getRadiusLabel } from 'components/Srp/RadiusModal';
import { screen } from 'components/styles/variables';
import { useLocationFilter, useRadiusFilter } from 'lib/hooks/useSrpFilters';
import { useContext } from 'react';
import SrpEventContext from 'contexts/SrpEventContext';

export interface LocationFilterProps {
  expanded?: boolean;
}

export default function LocationFilter(props: LocationFilterProps): JSX.Element {
  const { t } = useTranslation();
  const logger = useEvents();
  const { sourcePage } = useContext(SrpEventContext);
  const radiusFilter = useRadiusFilter();
  const locationFilter = useLocationFilter();

  const location = (
    <LocationInputNoDialog
      onInputTap={(): Promise<void> => logger.log(EnterCityZipTapEvent({ sourcePage }))}
      onCurrentLocationTap={(): Promise<void> =>
        logger.log(CurrentLocationTapEvent({ sourcePage }))
      }
      onSelectCityTap={(): Promise<void> => logger.log(SelectCityTapEvent({ sourcePage }))}
    />
  );

  const radius = (
    <RadiusModal
      onListItemClick={(): Promise<void> =>
        logger.log(
          RadiusTapEvent({
            feature: Features.locationRadius,
            sourcePage,
          })
        )
      }
    />
  );

  const locationLabel = getLocationLabel(t, locationFilter.value);
  const radiusLabel = getRadiusLabel(t, radiusFilter.value);

  return (
    <>
      <div>
        <FilterContainer
          title={t('srp.locationFilter.locationAndRadius')}
          expanded={props.expanded}
          onClick={(isExpanding: boolean): Promise<void> =>
            logger.log(ExpandCollapseTap({ feature: Features.cityZip, isExpanding, sourcePage }))
          }
        >
          <div className="location-modal-container">
            <ModalDropdownContainer
              placeHolderTitle={locationLabel}
              handleClick={(): Promise<void> => logger.log(EditLocationTapEvent({ sourcePage }))}
              fullHeight
            >
              {location}
            </ModalDropdownContainer>

            <ModalDropdownContainer
              placeHolderTitle={radiusLabel}
              isSmallerModal={true}
              handleClick={(): Promise<void> => logger.log(DistanceListTapEvent({ sourcePage }))}
            >
              {radius}
            </ModalDropdownContainer>
          </div>

          <div className="location-dropdown-container">
            <DropdownContainer title={t('srp.locationFilter.selectLocation')} resetsOnClose>
              {location}
            </DropdownContainer>

            <DropdownContainer title={radiusLabel} resetsOnClose>
              {radius}
            </DropdownContainer>
          </div>
        </FilterContainer>
      </div>
      <style jsx>{`
        .location-modal-container {
          display: flex;
          flex-direction: column;
        }

        @media (min-width: ${screen.mobileMax}) {
          .location-modal-container {
            display: none;
          }
        }

        @media (max-width: ${screen.mobileMax}) {
          .location-dropdown-container {
            display: none;
          }
        }
      `}</style>
    </>
  );
}
